import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Modal, ModalField } from '@/components/Modal';
import { errorField, saveMessage } from '@/lib/apps';
import { useCreatePost } from '@/lib/posts';
import { slugify, topicName, useAdminTopics } from '@/lib/topics';

/**
 * Starting a post.
 *
 * An English title and an address, and nothing else — everything the editor
 * offers is what the editor is for. The same two questions `NewAppModal` asks,
 * because they are the two that cannot be filled in later: the title is what
 * the post is called in the list before anybody opens it, and the address is
 * fixed the moment the post goes public.
 *
 * It creates a draft and goes straight there. The alternative — a modal that
 * closes onto the list — makes somebody find the row they just made.
 */
export function NewPostModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const create = useCreatePost();
  const topics = useAdminTopics();

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [topicId, setTopicId] = useState('');

  /*
   * The slug follows the title until somebody edits it, then stops for good.
   * Without the latch, correcting the slug and then adding a character to the
   * title silently throws the correction away — and the address the post ends
   * up with is not the one that was asked for.
   */
  const [slugEdited, setSlugEdited] = useState(false);

  const editTitle = (value: string) => {
    setTitle(value);
    if (!slugEdited) setSlug(slugify(value));
  };


  const failed = create.isError ? saveMessage(create.error) : null;
  const failedField = create.isError ? errorField(create.error) : null;


  return (
    <Modal
      title="New post"
      context="created as a draft"
      submitLabel="Create draft"
      submitting={create.isPending}
      canSubmit={Boolean(title.trim() && slug.trim())}
      error={failedField === 'slug' ? null : failed}
      onClose={onClose}
      onSubmit={() =>
        create.mutate(
          { slug: slug.trim(), title: title.trim(), topicId: topicId || null },
          { onSuccess: (post) => navigate(`/blog/${post.id}`) },
        )
      }
    >
      <ModalField label="Title · English">
        <input
          aria-label="Title · English"
          value={title}
          placeholder="Why we run the model on your device"
          onChange={(event) => editTitle(event.target.value)}
          className="field field-modal"
        />
      </ModalField>

      <ModalField label="Address">
        <input
          aria-label="Address"
          value={slug}
          placeholder="why-we-run-the-model-on-your-device"
          onChange={(event) => {
            setSlugEdited(true);
            setSlug(event.target.value);
          }}
          className="field field-mono field-modal"
        />
        {/*
          Said here, before the post exists, because after it has been public
          this is the one field that can no longer be corrected.
        */}
        <span className="text-[11px] leading-[1.5]" style={{ color: 'var(--dim)' }}>
          /blog/{slug || '…'} — fixed once the post has been published.
        </span>
        {failedField === 'slug' && failed ? (
          <span className="text-[11.5px]" role="alert" style={{ color: 'var(--danger)' }}>
            {failed}
          </span>
        ) : null}
      </ModalField>

      <ModalField label="Topic · optional">
        <select
          value={topicId}
          onChange={(event) => setTopicId(event.target.value)}
          className="field field-modal"
        >
          <option value="">Decide later</option>
          {(topics.data ?? []).map((topic) => (
            <option key={topic.id} value={topic.id}>
              {topicName(topic.name)}
            </option>
          ))}
        </select>
      </ModalField>
    </Modal>
  );
}
